// "Crea PDF": alternativa alla stampa diretta della pagina web (window.print()).
// Dal telefono la stampa diretta di una pagina A4 spesso non produce nulla o
// produce un foglio sbagliato (iOS aggiunge intestazioni, alcuni Android
// ignorano il formato pagina): un PDF vero, con il formato fisico esatto, si
// apre e si stampa invece in modo affidabile ovunque.
// - Documenti A4 (quadretti QR, libro ingredienti): il PDF si crea qui nel
//   browser dall'anteprima già a schermo (elementToPdf).
// - Etichette e cartellini: il PDF lo genera già il backend (label_pdf.py),
//   si scarica e si consegna con lo stesso flusso (openServerPdfFile).
// Le librerie sono caricate solo al primo "Crea PDF", per non appesantire l'app.
import api from "./api";
import { isIOS } from "./platform";

const BLOB_URL_LIFETIME_MS = 5 * 60 * 1000;
// Larghezza fissa di impaginazione (A4 a 96 dpi) e finestra "desktop" per le
// classi responsive: il PDF ha la stessa impaginazione da telefono e da PC.
const RENDER_WIDTH_PX = 794;
const RENDER_WINDOW_WIDTH_PX = 1024;
// Oltre ~16 milioni di pixel Safari iOS non disegna più il canvas (resta bianco).
const MAX_CANVAS_PIXELS = 12_000_000;
const CONTINUATION_MARGIN_PX = 38;

const isAndroid = () => typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

// Va chiamata in modo sincrono nel click (prima di ogni await): su iOS Safari
// blocca le finestre aperte dopo un'attesa asincrona.
export function preparePdfWindow() {
  return isIOS() ? window.open("", "_blank") : null;
}

// Consegna il PDF: Android lo scarica (Chrome non apre i PDF in una scheda;
// si apre dalla notifica di download e si stampa), iPhone/iPad e PC lo aprono
// in una nuova scheda, da cui si stampa o si condivide.
export function deliverPdf(blob, fileName, win = null) {
  const url = URL.createObjectURL(blob);
  if (isAndroid()) {
    if (win) win.close();
    const link = Object.assign(document.createElement("a"), { href: url, download: fileName });
    document.body.appendChild(link); link.click(); link.remove();
  } else if (win) {
    win.location.href = url;
  } else if (!window.open(url, "_blank")) {
    const link = Object.assign(document.createElement("a"), { href: url, download: fileName });
    document.body.appendChild(link); link.click(); link.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_LIFETIME_MS);
}

// PDF già generato dal backend (etichette, cartellini): GET o POST.
export async function openServerPdfFile(path, { method = "get", body, params, fileName = "documento.pdf", win = null } = {}) {
  try {
    const { data } = method === "post"
      ? await api.post(path, body, { params, responseType: "blob" })
      : await api.get(path, { params, responseType: "blob" });
    deliverPdf(data, fileName, win);
  } catch (error) {
    if (win) win.close();
    throw error;
  }
}

// Crea un PDF dall'elemento indicato (deve essere visibile a schermo).
// - widthMm/heightMm: formato della pagina (default A4 verticale).
// - pageSelector: se presente, ogni elemento che corrisponde inizia una
//   pagina nuova (es. copertina e schede del libro ingredienti).
// - keepSelector: elementi da non tagliare a metà tra due pagine (es. i QR).
// - renderWidthPx: larghezza di impaginazione (default A4); le etichette
//   piccole usano la larghezza della loro anteprima, con scala più alta.
export async function elementToPdf(element, { fileName = "documento.pdf", widthMm = 210, heightMm = 297, pageSelector, keepSelector = ".break-inside-avoid", renderWidthPx = RENDER_WIDTH_PX, win = null } = {}) {
  try {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
    const pdf = new jsPDF({ unit: "mm", format: [widthMm, heightMm], orientation: widthMm > heightMm ? "landscape" : "portrait", compress: true });
    const pageHeightPx = Math.round(renderWidthPx * (heightMm / widthMm));
    const groups = pageSelector ? Array.from(element.querySelectorAll(pageSelector)) : [element];
    let first = true;
    for (const target of (groups.length ? groups : [element])) {
      const layout = { height: 0, keep: [] };
      // Scala più alta possibile restando sotto il limite di pixel dei
      // telefoni; l'altezza a schermo (su telefono più stretto, quindi più
      // alta) è una stima prudente di quella finale.
      const estimatedHeight = Math.max(target.scrollHeight, pageHeightPx);
      const scale = Math.max(1, Math.min(2 * RENDER_WIDTH_PX / renderWidthPx, Math.sqrt(MAX_CANVAS_PIXELS / (renderWidthPx * estimatedHeight))));
      const canvas = await html2canvas(target, {
        scale,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        windowWidth: RENDER_WINDOW_WIDTH_PX,
        onclone: (doc, clone) => {
          Object.assign(clone.style, { width: `${renderWidthPx}px`, maxWidth: "none", margin: "0", boxShadow: "none" });
          // Gli antenati con overflow nascosto/scroll non devono tagliare la copia.
          for (let node = clone.parentElement; node && node !== doc.body; node = node.parentElement) node.style.overflow = "visible";
          const top = clone.getBoundingClientRect().top;
          layout.height = clone.scrollHeight;
          layout.keep = Array.from(clone.querySelectorAll(keepSelector)).map((item) => {
            const rect = item.getBoundingClientRect();
            return { top: rect.top - top, bottom: rect.bottom - top };
          });
        },
      });
      const ratio = canvas.width / renderWidthPx;
      const total = layout.height || canvas.height / ratio;

      for (let start = 0; start < total - 1;) {
        // Le pagine di continuazione hanno un margine in alto (le prime
        // hanno già il padding del foglio).
        const topPx = start > 0 ? CONTINUATION_MARGIN_PX : 0;
        let end = Math.min(start + pageHeightPx - topPx, total);
        if (end < total) {
          // Sposta il taglio sopra l'elemento che verrebbe diviso a metà.
          const cut = layout.keep.filter((box) => box.top > start + 1 && box.top < end && box.bottom > end);
          if (cut.length) end = Math.min(...cut.map((box) => box.top));
        }
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = Math.max(1, Math.round((end - start) * ratio));
        const context = slice.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, slice.width, slice.height);
        context.drawImage(canvas, 0, Math.round(start * ratio), slice.width, slice.height, 0, 0, slice.width, slice.height);
        if (!first) pdf.addPage([widthMm, heightMm], widthMm > heightMm ? "landscape" : "portrait");
        first = false;
        const mmPerPx = widthMm / renderWidthPx;
        pdf.addImage(slice.toDataURL("image/jpeg", 0.9), "JPEG", 0, topPx * mmPerPx, widthMm, (end - start) * mmPerPx);
        start = end;
      }
    }
    deliverPdf(pdf.output("blob"), fileName, win);
  } catch (error) {
    if (win) win.close();
    throw error;
  }
}

export const pdfFileName = (...parts) => `${parts.filter(Boolean).join("-").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "documento"}.pdf`;
